import React, { KeyboardEvent } from 'react';
import { noop } from 'lodash';
import { assignInlineVars, Box, tokens } from '@fiverr-private/theme';
import { Container } from '@fiverr-private/layout_components';
import Checkbox from '../../Checkbox';
import { ChoiceGroupItemProps } from '../types';
import { formsTokenColors } from '../../shared/formsTokenColors';
import useChoiceGroupItem from './hooks/useChoiceGroupItem';
import { generateStylingProps } from './style';
import { boxShadowClass, boxShadowColor } from './styles.ve.css';

function ChoiceGroupItem<T>({
  children,
  onClick: onClickProp = noop,
  value,
  disabled = false,
  checkboxPositioning = 'static',
  ...restProps
}: ChoiceGroupItemProps<T>) {
  const { isSelected, onClick, multiple, size, fill } = useChoiceGroupItem({ value, onClickProp, disabled });
  const { containerStylingProps, checkboxStylingProps } = generateStylingProps({ checkboxPositioning, size });
  const borderColor = disabled ? formsTokenColors.disabledToggleBackgroundColor : formsTokenColors.interactiveColor;

  const handleKeyDown = (event: KeyboardEvent) => {
    const { code } = event;
    if (code === 'Space' || code === 'Enter') {
      event.preventDefault();
      onClick(value);
    }
  };

  return (
    <Box
      display="flex"
      color={disabled ? formsTokenColors.disabledColor : 'grey_1200'}
      flex={fill ? 1 : undefined}
      position="relative"
      boxSizing="borderBox"
      borderWidth="sm"
      borderColor={{
        default: isSelected && !disabled ? formsTokenColors.interactiveColor : formsTokenColors.borderColor,
        hover: borderColor,
      }}
      outlineColor={{
        focusVisible: formsTokenColors.interactiveColor,
      }}
      outlineStyle={{
        focusVisible: 'solid',
      }}
      outlineWidth={{
        focusVisible: 'md',
      }}
      outlineOffset={{
        focusVisible: 'sm',
      }}
      borderStyle="solid"
      borderRadius="lg"
      cursor={disabled ? 'default' : 'pointer'}
      backgroundColor={disabled ? formsTokenColors.disabledFieldBackgroundColor : 'background'}
      fontSize="b_md"
      lineHeight="b_md"
      tabIndex={disabled ? -1 : 0}
      className={isSelected ? boxShadowClass : undefined}
      onKeyDown={handleKeyDown}
      onClick={() => {
        onClick(value);
      }}
      role="button"
      aria-selected={isSelected}
      style={assignInlineVars({ [boxShadowColor]: tokens.colors[borderColor] })}
      {...containerStylingProps}
      {...restProps}
    >
      <Container opacity={disabled ? '0.6' : undefined} flex={fill ? 1 : undefined}>
        {typeof children === 'function' ? children({ isSelected }) : children}
      </Container>
      {multiple && (
        <Container display="flex" marginLeft="auto" paddingLeft="3" {...checkboxStylingProps}>
          <Checkbox
            onClick={(event) => {
              event.stopPropagation();
            }}
            onChange={(event) => {
              event.stopPropagation();
            }}
            disabled={disabled}
            checked={isSelected}
            tabIndex={-1}
            disableCheckmarkMargin
          />
        </Container>
      )}
    </Box>
  );
}

ChoiceGroupItem.displayName = 'ChoiceGroupItem';

export default ChoiceGroupItem;
